import client from 'src/utils/client';

type GetProviderType = (providerId: string) => Promise<Provider>;
export const getProvider: GetProviderType = (providerId) =>
  client.get(`providers/${providerId}`).json();

type GetProvidersType = (
  searchParams?: QueryParams
) => Promise<PaginationResponse<Provider>>;
export const getProviders: GetProvidersType = (searchParams) =>
  client.get(`providers/`, { searchParams }).json();

type ProviderSelectOption = {
  id: number;
  name: string;
  company_name?: string | null;
  label?: string;
};
type GetProviderSelectOptionsType = (
  searchParams?: QueryParams
) => Promise<ProviderSelectOption[]>;
export const getProviderSelectOptions: GetProviderSelectOptionsType = (
  searchParams
) => client.get(`providers/select-options`, { searchParams }).json();

type GetProviderTypeOptionsType = () => Promise<ProviderTypeOption[]>;
export const getProviderTypeOptions: GetProviderTypeOptionsType = () =>
  client.get(`providers/provider-type-options`).json();

type CreateProviderTypeOptionType = (
  payload: Pick<ProviderTypeOption, 'name'>
) => Promise<ProviderTypeOption>;
export const createProviderTypeOption: CreateProviderTypeOptionType = (
  payload
) => client.post(`providers/provider-type-options`, { json: payload }).json();

type UpdateProviderTypeOptionType = (
  optionId: number,
  payload: Partial<Pick<ProviderTypeOption, 'name' | 'is_active'>>
) => Promise<ProviderTypeOption>;
export const updateProviderTypeOption: UpdateProviderTypeOptionType = (
  optionId,
  payload
) =>
  client
    .put(`providers/provider-type-options/${optionId}`, { json: payload })
    .json();

type DeactivateProviderTypeOptionType = (
  optionId: number
) => Promise<ProviderTypeOption>;
export const deactivateProviderTypeOption: DeactivateProviderTypeOptionType = (
  optionId
) =>
  client.post(`providers/provider-type-options/${optionId}/deactivate`).json();

type GetProvidersExpirationType = (
  searchParams?: QueryParams
) => Promise<PaginationResponse<ProviderExpiration>>;

export const getProvidersExpiration: GetProvidersExpirationType = (
  searchParams
) => client.get(`providers/expiration_list`, { searchParams }).json();

export const getProvidersFollowup: GetProvidersExpirationType = (
  searchParams
) => client.get(`providers/followup_list`, { searchParams }).json();

type GetAgreementsType = (
  providerId: number,
  searchParams?: QueryParams
) => Promise<PaginationResponse<Agreement>>;

export const getAgreements: GetAgreementsType = (providerId, searchParams) =>
  client
    .get(`providers/${providerId}/agreements-list`, { searchParams })
    .json();

type GetAgreementStatusHistoryType = (
  providerId: number,
  searchParams?: QueryParams
) => Promise<PaginationResponse<ProviderStatusHistory>>;
export const getAgreementStatusHistory: GetAgreementStatusHistoryType = (
  providerId,
  searchParams
) =>
  client.get(`providers/${providerId}/status-history`, { searchParams }).json();

type GetSimpleProvidersType = (
  providerId: number,
  searchParams?: QueryParams
) => Promise<PaginationResponse<SimpleProvider>>;

export const getSimpleProvidersType: GetSimpleProvidersType = (
  providerId,
  searchParams
) =>
  client
    .get(`providers/${providerId}/agreements-simple-list`, { searchParams })
    .json();

type TransferProviderResponse = {
  files_updated: number;
  managements_updated: number;
};
type TransferProviderDataType = (
  current_provider_id?: number,
  target_provider_id?: number,
  target_company_id?: number
) => Promise<TransferProviderResponse>;
export const transferProviderDataType: TransferProviderDataType = (
  current_provider_id,
  target_provider_id,
  target_company_id
) =>
  client
    .post(`providers/transfer-provider-data`, {
      json: {
        current_provider_id,
        target_provider_id,
        target_company_id
      }
    })
    .json();

type UpdateAgreementType = (agreement: SimpleAgreement) => Promise<void>;
export const updateAgreement: UpdateAgreementType = (agreement) =>
  client.post(`providers/agreement-update`, { json: agreement }).json();

type PreviewAutomaticAgreementRenewalType = (
  agreementId: number
) => Promise<AgreementRenewalPreview>;
export const previewAutomaticAgreementRenewal: PreviewAutomaticAgreementRenewalType = (
  agreementId
) =>
  client
    .post(`providers/agreements/${agreementId}/automatic-renewal/preview`)
    .json();

type ActivateAutomaticAgreementRenewalType = (
  agreementId: number,
  requestId: string,
  previewToken: string,
  observation?: string
) => Promise<AgreementRenewalActivationResponse>;
export const activateAutomaticAgreementRenewal: ActivateAutomaticAgreementRenewalType = (
  agreementId,
  requestId,
  previewToken,
  observation
) =>
  client
    .post(`providers/agreements/${agreementId}/automatic-renewal/activate`, {
      json: {
        request_id: requestId,
        preview_token: previewToken,
        observation
      }
    })
    .json();

type DeactivateAutomaticAgreementRenewalType = (
  agreementId: number
) => Promise<{ agreement_id: number; automatic_renewal: boolean }>;
export const deactivateAutomaticAgreementRenewal: DeactivateAutomaticAgreementRenewalType = (
  agreementId
) =>
  client
    .post(`providers/agreements/${agreementId}/automatic-renewal/deactivate`)
    .json();

type ResendAgreementRenewalEmailType = (
  agreementId: number,
  renewalId: number
) => Promise<AgreementRenewalResult>;
export const resendAgreementRenewalEmail: ResendAgreementRenewalEmailType = (
  agreementId,
  renewalId
) =>
  client
    .post(
      `providers/agreements/${agreementId}/automatic-renewals/${renewalId}/resend-email`
    )
    .json();

type GetAgreementRenewalHistoryType = (
  providerId: number,
  agreementId: number,
  searchParams?: QueryParams
) => Promise<PaginationResponse<AgreementRenewalHistoryEntry>>;
export const getAgreementRenewalHistory: GetAgreementRenewalHistoryType = (
  providerId,
  agreementId,
  searchParams
) =>
  client
    .get(`providers/${providerId}/agreements/${agreementId}/renewal-history`, {
      searchParams
    })
    .json();

type CreateProviderType = (provider: BaseProvider) => Promise<Provider>;
export const createProvider: CreateProviderType = (provider) =>
  client.post(`providers/`, { json: provider }).json();

type DeleteProviderType = (providerId: number) => Promise<void>;
export const deleteProvider: DeleteProviderType = (providerId) =>
  client.delete(`providers/${providerId}`).json();

type ArchiveProviderType = (providerId: number) => Promise<Provider>;
export const archiveProvider: ArchiveProviderType = (providerId) =>
  client.post(`providers/${providerId}/archive`).json();

type GetProviderCustomStateType = (
  providerId: number
) => Promise<ProviderCustomState>;
export const getProviderCustomState: GetProviderCustomStateType = (
  providerId
) => client.get(`providers/${providerId}/custom-state`).json();

type UpdateProviderCustomStatePayload = {
  option_id?: number | null;
};
type UpdateProviderCustomStateType = (
  providerId: number,
  payload: UpdateProviderCustomStatePayload
) => Promise<ProviderCustomState>;
export const updateProviderCustomState: UpdateProviderCustomStateType = (
  providerId,
  payload
) =>
  client.put(`providers/${providerId}/custom-state`, { json: payload }).json();

type GetProviderCustomStateOptionsType = () => Promise<
  ProviderCustomStateOption[]
>;
export const getProviderCustomStateOptions: GetProviderCustomStateOptionsType = () =>
  client.get(`providers/custom-state-options`).json();

type ProviderCustomStateOptionPayload = {
  name: string;
  is_active?: boolean;
};
type CreateProviderCustomStateOptionType = (
  payload: ProviderCustomStateOptionPayload
) => Promise<ProviderCustomStateOption>;
export const createProviderCustomStateOption: CreateProviderCustomStateOptionType = (
  payload
) => client.post(`providers/custom-state-options`, { json: payload }).json();

type UpdateProviderCustomStateOptionType = (
  optionId: number,
  payload: Partial<ProviderCustomStateOptionPayload>
) => Promise<ProviderCustomStateOption>;
export const updateProviderCustomStateOption: UpdateProviderCustomStateOptionType = (
  optionId,
  payload
) =>
  client
    .put(`providers/custom-state-options/${optionId}`, { json: payload })
    .json();

type DeactivateProviderCustomStateOptionType = (
  optionId: number
) => Promise<ProviderCustomStateOption>;
export const deactivateProviderCustomStateOption: DeactivateProviderCustomStateOptionType = (
  optionId
) =>
  client.post(`providers/custom-state-options/${optionId}/deactivate`).json();

type UpdateProviderType = (
  providerId: number,
  provider: Partial<Provider>
) => Promise<Provider>;
export const updateProvider: UpdateProviderType = (providerId, provider) =>
  client.put(`providers/${providerId}`, { json: provider }).json();

type UpdateProviderStatusType = (
  deviceStatus: ProviderStatusUpdate
) => Promise<void>;
export const updateProviderStatus: UpdateProviderStatusType = (
  providerStatus
) => client.post(`providers/status-update`, { json: providerStatus }).json();

type GetStatesType = () => Promise<State[]>;
export const getStates: GetStatesType = () =>
  client.get(`providers/states`).json();

type GetStateCitiesType = (stateId: number) => Promise<City[]>;
export const getStateCities: GetStateCitiesType = (stateId) =>
  client.get(`providers/states/${stateId}/cities`).json();

type GetSpecialtiesType = (searchParams?: QueryParams) => Promise<Specialty[]>;
export const getSpecialties: GetSpecialtiesType = (searchParams) =>
  client.get(`providers/current-specialties`, { searchParams }).json();

type GetProviderCompaniesType = (
  searchParams?: QueryParams
) => Promise<Company[]>;
export const getProviderCompanies: GetProviderCompaniesType = (searchParams) =>
  client.get(`providers/companies`, { searchParams }).json();

type GetProvidersExcelType = (searchParams?: QueryParams) => Promise<Blob>;
export const getProvidersExcel: GetProvidersExcelType = (searchParams) =>
  client.get(`providers/excel`, { searchParams, cache: 'no-store' }).blob();
export const getProvidersSimpleExcel: GetProvidersExcelType = (searchParams) =>
  client
    .get(`providers/simple-excel`, { searchParams, cache: 'no-store' })
    .blob();
export const getExpirationProvidersExcel: GetProvidersExcelType = (
  searchParams
) => client.get(`providers/expiration-excel`, { searchParams }).blob();
type UploadProvidersExcelType = (update: FormData) => Promise<void>;
export const uploadProvidersExcel: UploadProvidersExcelType = (update) =>
  client.post(`providers/excel`, { body: update }).json();

export const getManagementsExcel: GetProvidersExcelType = (searchParams) =>
  client.get(`providers/get_managements_excel`, { searchParams }).blob();

export const getAuditReport: GetProvidersExcelType = (searchParams) =>
  client.get(`providers/excel/audit-excel`, { searchParams }).blob();

type GetProviderFilesType = (
  providerId: number,
  searchParams?: QueryParams
) => Promise<PaginationResponse<ProviderFile>>;
export const getProviderFiles: GetProviderFilesType = (
  providerId,
  searchParams
) => client.get(`providers/${providerId}/get_files`, { searchParams }).json();

type GetHistoryProviderFilesType = (
  searchParams?: QueryParams
) => Promise<PaginationResponse<HistoryProviderFile>>;
export const getHistoryFiles: GetHistoryProviderFilesType = (searchParams) =>
  client.get(`providers/get-history-files`, { searchParams }).json();

type GetProviderFileBlobType = (
  providerId: number,
  fileId: number
) => Promise<Blob>;
export const getProviderFileBlob: GetProviderFileBlobType = (
  providerId,
  fileId
) => client.get(`providers/${providerId}/download_file/${fileId}`).blob();

type GetProviderFilesZipBlobType = (providerId: number) => Promise<Blob>;
export const getProviderFilesZipBlob: GetProviderFilesZipBlobType = (
  providerId
) => client.get(`providers/${providerId}/download_files_zip`).blob();

type CreateProviderFileType = (
  providerId: number,
  providerFile: FormData
) => Promise<void>;
export const createProviderFile: CreateProviderFileType = (
  providerId,
  providerFile
) =>
  client
    .post(`providers/${providerId}/create_file`, { body: providerFile })
    .json();
export const UpdateFile: CreateProviderFileType = (providerId, providerFile) =>
  client
    .patch(`providers/${providerId}/update-only-file`, { body: providerFile })
    .json();
type DeleteProviderFileType = (
  providerId: number,
  file_id: number
) => Promise<void>;
export const deleteProviderFile: DeleteProviderFileType = (
  providerId,
  file_id
) =>
  client
    .delete(`providers/${providerId}/delete_file`, { json: { file_id } })
    .json();
type UpdateProviderFileType = (
  providerId: number,
  providerFile: FormData
) => Promise<void>;
export const updateProviderFile: UpdateProviderFileType = (
  providerId,
  providerFile
) =>
  client
    .put(`providers/${providerId}/update_file`, { body: providerFile })
    .json();

type GetProviderManagementsType = (
  providerId: number,
  searchParams?: QueryParams
) => Promise<PaginationResponse<ProviderManagement>>;
export const getProviderManagements: GetProviderManagementsType = (
  providerId,
  searchParams
) =>
  client
    .get(`providers/${providerId}/get_managements`, { searchParams })
    .json();
type CreateProviderManagementType = (
  providerId: number,
  ProviderManagement: FormData
) => Promise<any>;
export const createProviderManagement: CreateProviderManagementType = (
  providerId,
  ProviderManagement
) =>
  client
    .post(`providers/${providerId}/create_management`, {
      body: ProviderManagement
    })
    .json();
type DeleteProviderManagementType = (
  providerId: number,
  management_id: number
) => Promise<void>;
export const deleteProviderManagement: DeleteProviderManagementType = (
  providerId,
  management_id
) =>
  client
    .delete(`providers/${providerId}/delete_management`, {
      json: { management_id }
    })
    .json();
type UpdateProviderManagementType = (
  providerId: number,
  management: FormData
) => Promise<ProviderManagement>;
export const updateProviderManagement: UpdateProviderManagementType = (
  providerId,
  management
) =>
  client
    .put(`providers/${providerId}/update_management`, {
      body: management
    })
    .json();
export const finalizeProviderManagement: UpdateProviderManagementType = (
  providerId,
  management
) =>
  client
    .put(`providers/${providerId}/finalize_management`, {
      body: management
    })
    .json();

type UploadProviderManagementFilesType = (
  providerId: number,
  managementId: number,
  files: FormData
) => Promise<void>;
export const uploadProviderManagementFiles: UploadProviderManagementFilesType = (
  providerId,
  managementId,
  files
) =>
  client
    .post(`providers/${providerId}/managements/${managementId}/upload-files`, {
      body: files
    })
    .json();

type DownloadProviderManagementFilesType = (
  providerId: number,
  managementId: number
) => Promise<Blob>;
export const downloadProviderManagementFiles: DownloadProviderManagementFilesType = (
  providerId,
  managementId
) =>
  client
    .get(`providers/${providerId}/managements/${managementId}/download-files`)
    .blob();

type CreateProviderUserInviteType = (
  providerId: number,
  payload: { email: string }
) => Promise<any>;
export const createProviderUserInvite: CreateProviderUserInviteType = (
  providerId,
  payload
) =>
  client
    .post(`providers/${providerId}/create_provider_user_invite`, {
      json: payload
    })
    .json();

type ProviderUserInviteStatus = {
  should_prompt_provider_user_invite: boolean;
  existing_user_id?: number | null;
  email?: string;
};
type GetProviderUserInviteStatusType = (
  providerId: number
) => Promise<ProviderUserInviteStatus>;
export const getProviderUserInviteStatus: GetProviderUserInviteStatusType = (
  providerId
) => client.get(`providers/${providerId}/provider_user_invite_status`).json();
type GenericDocument = {
  id: number;
  name: string;
  code?: string | null;
  is_active?: boolean;
  is_required?: boolean;
  required_companies?: number[];
  requires_expiration_date?: boolean;
  requires_legal_expiration?: boolean;
  requires_internal_expiration?: boolean;
  provider_types_by_company?: Record<string, string[]>;
  exception_providers?: number[];
  required_providers?: number[];
  legal_expiration_providers?: number[];
  internal_expiration_day_options?: number[];
  internal_expiration_provider_days?: Record<string, number[]>;
  applies_to_provider_type?: string | null;
};

type GetGenericDocumentsType = (
  searchParams?: QueryParams
) => Promise<GenericDocument[]>;

export const getGenericDocuments: GetGenericDocumentsType = (searchParams) =>
  client.get(`providers/generic-documents`, { searchParams }).json();

type GenericDocumentPayload = {
  name: string;
  code?: string | null;
  is_required: boolean;
  required_companies?: number[];
  requires_expiration_date: boolean;
  requires_legal_expiration?: boolean;
  requires_internal_expiration?: boolean;
  provider_types_by_company?: Record<string, string[]>;
  exception_providers?: number[];
  required_providers?: number[];
  legal_expiration_providers?: number[];
  internal_expiration_day_options?: number[];
  internal_expiration_provider_days?: Record<string, number[]>;
  applies_to_provider_type?: string | null;
};

export const getAllGenericDocuments = () =>
  client
    .get(`providers/generic-documents`, {
      searchParams: { include_inactive: 'true' }
    })
    .json<GenericDocument[]>();

export const createGenericDocument = (payload: GenericDocumentPayload) =>
  client
    .post(`providers/generic-documents`, { json: payload })
    .json<GenericDocument>();

export const updateGenericDocument = (
  documentId: number,
  payload: Partial<GenericDocumentPayload>
) =>
  client
    .put(`providers/generic-documents/${documentId}`, { json: payload })
    .json<GenericDocument>();

export const activateGenericDocument = (documentId: number) =>
  client
    .post(`providers/generic-documents/${documentId}/activate`)
    .json<GenericDocument>();

export const deactivateGenericDocument = (documentId: number) =>
  client
    .post(`providers/generic-documents/${documentId}/deactivate`)
    .json<GenericDocument>();

type InternalExpirationDayOption = { id: number; name: string };
export const getGenericDocumentInternalDayOptions = () =>
  client
    .get(`providers/generic-documents/internal-day-options`)
    .json<InternalExpirationDayOption[]>();

type GetNotificationsType = (
  searchParams?: QueryParams
) => Promise<NotificationsResponse>;
export const getNotifications: GetNotificationsType = (searchParams) =>
  client.get(`providers/get_notifications`, { searchParams }).json();

export const getResolvedNotifications: GetNotificationsType = (searchParams) =>
  client.get(`providers/resolved_notifications`, { searchParams }).json();

type NotificationsNewCountResponse = {
  new_count: number;
};

type GetNotificationsNewCountType = () => Promise<
  NotificationsNewCountResponse
>;
export const getNotificationsNewCount: GetNotificationsNewCountType = () =>
  client.get(`providers/get_notifications_new_count`).json();

type MarkNotificationsAsReadType = (
  notification_ids?: number[]
) => Promise<{
  updated_count: number;
}>;
export const markNotificationsAsRead: MarkNotificationsAsReadType = (
  notification_ids = []
) =>
  client
    .post(`providers/mark_notifications_as_read`, {
      json: { notification_ids }
    })
    .json();

type ResolveNotificationType = (
  notificationId: number
) => Promise<NotificationType>;
export const resolveNotification: ResolveNotificationType = (notificationId) =>
  client.post(`providers/${notificationId}/resolve`).json();

type GetManagementResultOptionsType = () => Promise<ManagementResultOption[]>;
export const getManagementResultOptions: GetManagementResultOptionsType = () =>
  client.get(`providers/management-result-options`).json();

type CreateManagementResultOptionType = (
  payload: Pick<
    ManagementResultOption,
    'name' | 'is_effective' | 'is_active' | 'requires_next_action'
  >
) => Promise<ManagementResultOption>;
export const createManagementResultOption: CreateManagementResultOptionType = (
  payload
) =>
  client
    .post(`providers/management-result-options`, {
      json: payload
    })
    .json();

type UpdateManagementResultOptionType = (
  optionId: number,
  payload: Partial<
    Pick<
      ManagementResultOption,
      'name' | 'is_effective' | 'is_active' | 'requires_next_action'
    >
  >
) => Promise<ManagementResultOption>;
export const updateManagementResultOption: UpdateManagementResultOptionType = (
  optionId,
  payload
) =>
  client
    .put(`providers/management-result-options/${optionId}`, {
      json: payload
    })
    .json();

type DeactivateManagementResultOptionType = (
  optionId: number
) => Promise<ManagementResultOption>;
export const deactivateManagementResultOption: DeactivateManagementResultOptionType = (
  optionId
) =>
  client
    .post(`providers/management-result-options/${optionId}/deactivate`)
    .json();

type GetDesistReasonOptionsType = () => Promise<DesistReasonOption[]>;
export const getDesistReasonOptions: GetDesistReasonOptionsType = () =>
  client.get(`providers/desist-reason-options`).json();

type CreateDesistReasonOptionType = (
  payload: Pick<DesistReasonOption, 'name' | 'is_active'>
) => Promise<DesistReasonOption>;
export const createDesistReasonOption: CreateDesistReasonOptionType = (
  payload
) =>
  client
    .post(`providers/desist-reason-options`, {
      json: payload
    })
    .json();

type UpdateDesistReasonOptionType = (
  optionId: number,
  payload: Partial<Pick<DesistReasonOption, 'name' | 'is_active'>>
) => Promise<DesistReasonOption>;
export const updateDesistReasonOption: UpdateDesistReasonOptionType = (
  optionId,
  payload
) =>
  client
    .put(`providers/desist-reason-options/${optionId}`, {
      json: payload
    })
    .json();

type DeactivateDesistReasonOptionType = (
  optionId: number
) => Promise<DesistReasonOption>;
export const deactivateDesistReasonOption: DeactivateDesistReasonOptionType = (
  optionId
) =>
  client.post(`providers/desist-reason-options/${optionId}/deactivate`).json();

type GetManagementTypeOptionsType = () => Promise<ManagementTypeOption[]>;
export const getManagementTypeOptions: GetManagementTypeOptionsType = () =>
  client.get(`providers/management-type-options`).json();

type CreateManagementTypeOptionType = (
  payload: Pick<ManagementTypeOption, 'value' | 'name' | 'is_active'>
) => Promise<ManagementTypeOption>;
export const createManagementTypeOption: CreateManagementTypeOptionType = (
  payload
) =>
  client
    .post(`providers/management-type-options`, {
      json: payload
    })
    .json();

type UpdateManagementTypeOptionType = (
  optionId: number,
  payload: Partial<Pick<ManagementTypeOption, 'value' | 'name' | 'is_active'>>
) => Promise<ManagementTypeOption>;
export const updateManagementTypeOption: UpdateManagementTypeOptionType = (
  optionId,
  payload
) =>
  client
    .put(`providers/management-type-options/${optionId}`, {
      json: payload
    })
    .json();

type DeactivateManagementTypeOptionType = (
  optionId: number
) => Promise<ManagementTypeOption>;
export const deactivateManagementTypeOption: DeactivateManagementTypeOptionType = (
  optionId
) =>
  client
    .post(`providers/management-type-options/${optionId}/deactivate`)
    .json();

type GetRateOptionsType = () => Promise<RateOption[]>;
export const getRateOptions: GetRateOptionsType = () =>
  client.get(`providers/rate-options`).json();

type CreateRateOptionType = (
  payload: Pick<RateOption, 'value' | 'name' | 'is_active'>
) => Promise<RateOption>;
export const createRateOption: CreateRateOptionType = (payload) =>
  client
    .post(`providers/rate-options`, {
      json: payload
    })
    .json();

type UpdateRateOptionType = (
  optionId: number,
  payload: Partial<Pick<RateOption, 'value' | 'name' | 'is_active'>>
) => Promise<RateOption>;
export const updateRateOption: UpdateRateOptionType = (optionId, payload) =>
  client
    .put(`providers/rate-options/${optionId}`, {
      json: payload
    })
    .json();

type DeactivateRateOptionType = (optionId: number) => Promise<RateOption>;
export const deactivateRateOption: DeactivateRateOptionType = (optionId) =>
  client.post(`providers/rate-options/${optionId}/deactivate`).json();

type GetAgreementStatusOptionsType = () => Promise<AgreementStatusOption[]>;
export const getAgreementStatusOptions: GetAgreementStatusOptionsType = () =>
  client.get(`providers/agreement-status-options`).json();

type CreateAgreementStatusOptionType = (
  payload: Pick<
    AgreementStatusOption,
    'value' | 'name' | 'is_active' | 'is_geo_enabled'
  >
) => Promise<AgreementStatusOption>;
export const createAgreementStatusOption: CreateAgreementStatusOptionType = (
  payload
) =>
  client
    .post(`providers/agreement-status-options`, {
      json: payload
    })
    .json();

type UpdateAgreementStatusOptionType = (
  optionId: number,
  payload: Partial<
    Pick<
      AgreementStatusOption,
      'value' | 'name' | 'is_active' | 'is_geo_enabled'
    >
  >
) => Promise<AgreementStatusOption>;
export const updateAgreementStatusOption: UpdateAgreementStatusOptionType = (
  optionId,
  payload
) =>
  client
    .put(`providers/agreement-status-options/${optionId}`, {
      json: payload
    })
    .json();

type DeactivateAgreementStatusOptionType = (
  optionId: number
) => Promise<AgreementStatusOption>;
export const deactivateAgreementStatusOption: DeactivateAgreementStatusOptionType = (
  optionId
) =>
  client
    .post(`providers/agreement-status-options/${optionId}/deactivate`)
    .json();

type GetManagementChannelOptionsType = () => Promise<ManagementChannelOption[]>;
export const getManagementChannelOptions: GetManagementChannelOptionsType = () =>
  client.get(`providers/management-channel-options`).json();

type CreateManagementChannelOptionType = (
  payload: Pick<ManagementChannelOption, 'value' | 'name' | 'is_active'>
) => Promise<ManagementChannelOption>;
export const createManagementChannelOption: CreateManagementChannelOptionType = (
  payload
) =>
  client
    .post(`providers/management-channel-options`, {
      json: payload
    })
    .json();

type UpdateManagementChannelOptionType = (
  optionId: number,
  payload: Partial<
    Pick<ManagementChannelOption, 'value' | 'name' | 'is_active'>
  >
) => Promise<ManagementChannelOption>;
export const updateManagementChannelOption: UpdateManagementChannelOptionType = (
  optionId,
  payload
) =>
  client
    .put(`providers/management-channel-options/${optionId}`, {
      json: payload
    })
    .json();

type DeactivateManagementChannelOptionType = (
  optionId: number
) => Promise<ManagementChannelOption>;
export const deactivateManagementChannelOption: DeactivateManagementChannelOptionType = (
  optionId
) =>
  client
    .post(`providers/management-channel-options/${optionId}/deactivate`)
    .json();
